// Numeric formatting + masking helpers.
//
// Currency formatters (formatMoney, formatPercentage, formatAed) live next
// door in `currencyUtils.ts`. Anything that maps a number to a non-currency
// presentation string belongs here.

import { digitsOnly } from './stringUtils'

const TWO_DECIMAL_PRECISION = 100
const VISIBLE_ACCOUNT_DIGITS = 4
const VISIBLE_EMIRATES_ID_DIGITS = 4

const usDecimalFormatter = new Intl.NumberFormat('en-US')

/**
 * Rounds a number to 2 decimal places. Used by the currency formatters that
 * need to display "AED 1,234.56" cleanly without floating-point artefacts.
 */
export const roundToTwoDecimals = (value: number): number =>
  Math.round(value * TWO_DECIMAL_PRECISION) / TWO_DECIMAL_PRECISION

/**
 * Clamps `value` to the inclusive `[min, max]` range.
 * @example clamp(127, 0, 100) // 100
 * @example clamp(-5, 0, 100)  // 0
 */
export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Strips the country code and whitespace from a full mobile number,
 * returning only the national number.
 */
export function toNationalNumber(mobile: string, countryCode?: string | null): string {
  const stripped = countryCode ? mobile.replace(countryCode, '') : mobile
  return stripped.replace(/\s+/g, '').trim()
}

/**
 * Masks a bank account number, revealing only the last 4 digits.
 * @example maskAccountNumber('1234567890') // '•••• 7890'
 */
export function maskAccountNumber(accountNumber?: string | null): string {
  if (!accountNumber) return '••••'
  return `•••• ${accountNumber.slice(-VISIBLE_ACCOUNT_DIGITS)}`
}

/**
 * Masks an Emirates ID, revealing only the last 4 digits.
 *
 * A national identifier is not something a back-office row should carry in the
 * clear: the masked form keeps the screen safe to screenshot into a ticket, and
 * the full value is revealed deliberately, never by default.
 * @example maskEmiratesId('784199012345671') // '••••••••••• 5671'
 */
export function maskEmiratesId(emiratesId?: string | null): string {
  if (!emiratesId) return ''
  const digits = digitsOnly(emiratesId)
  // Empty rather than an em-dash: which glyph stands in for "nothing" is the
  // call site's decision, not a shared formatter's.
  if (digits.length <= VISIBLE_EMIRATES_ID_DIGITS) return digits
  const hidden = '•'.repeat(digits.length - VISIBLE_EMIRATES_ID_DIGITS)
  return `${hidden} ${digits.slice(-VISIBLE_EMIRATES_ID_DIGITS)}`
}

/**
 * Formats a number with US locale grouping ("1,234,567"). Returns "0" for
 * non-numeric input. Use `formatMoney` / `formatAed` for currency.
 */
export function formatToInternational(amount: number | string): string {
  const num = typeof amount === 'string' ? Number(amount) : amount
  if (isNaN(num)) return '0'
  return usDecimalFormatter.format(num)
}

// ── XRPL drops ──────────────────────────────────────────────────────────────
// XRPL account balances are denominated in *drops*, the ledger's integer minor
// unit: 1 XRP = 1,000,000 drops. They arrive as strings because the ledger's
// values are int64 and the total supply (10^17 drops) is two orders of magnitude
// past Number.MAX_SAFE_INTEGER — so `Number(drops) / 1e6` silently rounds, and
// the float divide adds artefacts of its own on top.
//
// The conversion below is therefore pure string arithmetic: shift the decimal
// point by moving where the string is cut, never by dividing. No `Number()` or
// `parseFloat` touches the value at any point.

const DROPS_DECIMAL_PLACES = 6

/** Human-readable XRP amount for an integer drops string, e.g. "1,234.5". */
export function formatXrpFromDrops(
  drops: string | number | null | undefined,
  fallback = '—',
): string {
  if (drops === null || drops === undefined || drops === '') return fallback

  const raw = String(drops).trim()
  const negative = raw.startsWith('-')
  const digits = raw.replace(/^[+-]/, '')
  // Drops are integral by definition. Anything else (a float, an address, a
  // stray unit suffix) is not a drops value and must not be guessed at.
  if (!/^\d+$/.test(digits)) return fallback

  // Pad so there is always at least one digit left of the decimal point, then
  // cut: the last six digits are the fraction, everything before is whole XRP.
  const padded = digits.padStart(DROPS_DECIMAL_PLACES + 1, '0')
  const cut = padded.length - DROPS_DECIMAL_PLACES
  const whole = padded.slice(0, cut).replace(/^0+(?=\d)/, '')
  // Trailing zeros carry no information at this precision — "1.500000" reads
  // worse than "1.5" and "1.000000" worse than "1".
  const fraction = padded.slice(cut).replace(/0+$/, '')

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const sign = negative && /[1-9]/.test(digits) ? '-' : ''
  return `${sign}${grouped}${fraction ? `.${fraction}` : ''}`
}

// ── Wire decimals and int64 byte counts ─────────────────────────────────────
// admin-service serialises DB `numeric` columns and `bigint` columns as JSON
// **strings** — token quantities, holding values and document file sizes all
// arrive that way. The reason is the same as for drops above: both exceed what
// an IEEE-754 double represents exactly, so `Number(value)` is a lossy
// conversion applied to a value that was exact on the wire.
//
// `formatXrpFromDrops` does not fit either of them: it *scales* an integer
// (drops → XRP, a fixed six-place shift) and rejects anything with a decimal
// point. A `tokenQuantity` of "1250.500000" is already in its display unit and
// carries its own point; a `fileSizeBytes` needs a unit chosen from its
// magnitude. Hence two more formatters, held to the same no-float discipline:
// every operation below is a string slice, never an arithmetic divide.

const THOUSANDS_GROUPING = /\B(?=(\d{3})+(?!\d))/g
const LEADING_ZEROS = /^0+(?=\d)/
const TRAILING_ZEROS = /0+$/

/** Group an all-digits integer string in thousands. No parsing involved. */
function groupDigits(digits: string): string {
  return digits.replace(LEADING_ZEROS, '').replace(THOUSANDS_GROUPING, ',')
}

/**
 * A wire decimal string, grouped and with its padding zeros trimmed —
 * `"1250.500000"` → `"1,250.5"`, `"3000.000000"` → `"3,000"`.
 *
 * Never coerced to a number, so a quantity past Number.MAX_SAFE_INTEGER prints
 * every digit it arrived with. Anything that is not a decimal literal (an id, a
 * unit suffix, an empty string) resolves to `fallback` rather than being guessed
 * at — the same posture `formatXrpFromDrops` takes.
 */
export function formatDecimalString(
  value: string | number | null | undefined,
  fallback = '—',
): string {
  if (value === null || value === undefined || value === '') return fallback

  const raw = String(value).trim()
  const negative = raw.startsWith('-')
  const unsigned = raw.replace(/^[+-]/, '')
  if (!/^\d+(\.\d+)?$/.test(unsigned)) return fallback

  // The regex above guarantees a whole part, but `noUncheckedIndexedAccess`
  // types the split as possibly-undefined — the default keeps that honest.
  const [wholeDigits = '0', fractionDigits = ''] = unsigned.split('.')
  const whole = groupDigits(wholeDigits)
  const fraction = fractionDigits.replace(TRAILING_ZEROS, '')
  // A negative zero is zero. Only a value with a non-zero digit keeps its sign.
  const sign = negative && /[1-9]/.test(unsigned) ? '-' : ''

  return `${sign}${whole}${fraction ? `.${fraction}` : ''}`
}

// SI (power-of-1000) units, because the decimal step is what a string slice can
// express exactly: shifting three digits is a slice, dividing by 1024 is not.
// Labelled kB / MB / GB accordingly — not KB, which conventionally means 1024.
const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB'] as const
const BYTE_UNIT_DIGITS = 3
const BYTE_FRACTION_DIGITS = 1

/**
 * A human-readable size for an int64 byte-count **string** — `"1450000"` →
 * `"1.5 MB"`.
 *
 * Pure string arithmetic: the unit is chosen from the digit count and the
 * decimal point is placed by slicing, so a multi-gigabyte size never passes
 * through a float. Non-integer input resolves to `fallback`.
 */
export function formatByteSize(bytes: string | number | null | undefined, fallback = '—'): string {
  if (bytes === null || bytes === undefined || bytes === '') return fallback

  const digits = String(bytes).trim()
  if (!/^\d+$/.test(digits)) return fallback

  const significant = digits.replace(LEADING_ZEROS, '')
  const unitIndex = Math.min(
    Math.floor((significant.length - 1) / BYTE_UNIT_DIGITS),
    BYTE_UNITS.length - 1,
  )
  if (unitIndex === 0) return `${significant} ${BYTE_UNITS[0]}`

  const shift = unitIndex * BYTE_UNIT_DIGITS
  const cut = significant.length - shift
  const whole = groupDigits(significant.slice(0, cut))
  const fraction = significant.slice(cut, cut + BYTE_FRACTION_DIGITS).replace(TRAILING_ZEROS, '')

  return `${whole}${fraction ? `.${fraction}` : ''} ${BYTE_UNITS[unitIndex]}`
}
